import { useEffect, useState } from 'react';
import { apiCall, ApiError } from '../utils/apiHelpers';
import { useAuth } from '../contexts/AuthContext';
import { AdminOrgPage } from './AdminOrgPage';
import { AdminReposPage } from './AdminReposPage';
import { AdminRolesPage } from './AdminRolesPage';
import { AdminUsersPage } from './AdminUsersPage';
import { AdminAIPage } from './AdminAIPage';
import { AdminAuditPage } from './AdminAuditPage';

type TechnicalLevel = 'novice' | 'intermediate' | 'expert';
type ResponseLength = 'concise' | 'standard' | 'thorough';

type SettingsTab = 'profile' | 'org' | 'repos' | 'roles' | 'users' | 'ai' | 'audit';

interface Props {
    onClose: () => void;
}

export function UserSettingsModal({ onClose }: Props) {
    const { userInfo, refresh } = useAuth();
    const isAdmin = userInfo?.role === 'admin';
    const [tab, setTab] = useState<SettingsTab>('profile');

    return (
        <div className="modal-overlay" onClick={onClose} role="presentation">
            <div className="modal-content modal-content--settings" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                <header className="modal-header">
                    <h2>Settings</h2>
                    <button className="btn btn--ghost" onClick={onClose} aria-label="Close">×</button>
                </header>
                <div className="settings-layout">
                    <nav className="settings-nav" role="tablist">
                        <button role="tab" aria-selected={tab === 'profile'} className={'settings-tab' + (tab === 'profile' ? ' settings-tab--active' : '')} onClick={() => setTab('profile')}>Profile</button>
                        {isAdmin && (
                            <>
                                <div className="settings-nav__heading">Admin</div>
                                <button role="tab" aria-selected={tab === 'org'} className={'settings-tab' + (tab === 'org' ? ' settings-tab--active' : '')} onClick={() => setTab('org')}>Organization</button>
                                <button role="tab" aria-selected={tab === 'repos'} className={'settings-tab' + (tab === 'repos' ? ' settings-tab--active' : '')} onClick={() => setTab('repos')}>Repos</button>
                                <button role="tab" aria-selected={tab === 'roles'} className={'settings-tab' + (tab === 'roles' ? ' settings-tab--active' : '')} onClick={() => setTab('roles')}>Roles</button>
                                <button role="tab" aria-selected={tab === 'users'} className={'settings-tab' + (tab === 'users' ? ' settings-tab--active' : '')} onClick={() => setTab('users')}>Users</button>
                                <button role="tab" aria-selected={tab === 'ai'} className={'settings-tab' + (tab === 'ai' ? ' settings-tab--active' : '')} onClick={() => setTab('ai')}>AI</button>
                                <button role="tab" aria-selected={tab === 'audit'} className={'settings-tab' + (tab === 'audit' ? ' settings-tab--active' : '')} onClick={() => setTab('audit')}>Audit</button>
                            </>
                        )}
                    </nav>
                    <div className="settings-body">
                        {tab === 'profile' && <ProfileTab onRefresh={refresh} />}
                        {tab === 'org' && isAdmin && <AdminOrgPage />}
                        {tab === 'repos' && isAdmin && <AdminReposPage />}
                        {tab === 'roles' && isAdmin && <AdminRolesPage />}
                        {tab === 'users' && isAdmin && <AdminUsersPage />}
                        {tab === 'ai' && isAdmin && <AdminAIPage />}
                        {tab === 'audit' && isAdmin && <AdminAuditPage />}
                    </div>
                </div>
            </div>
        </div>
    );
}

function ProfileTab({ onRefresh }: { onRefresh: () => Promise<void> }) {
    const { userInfo } = useAuth();
    const [technicalLevel, setTechnicalLevel] = useState<TechnicalLevel>(
        (userInfo?.preferences?.technicalLevel as TechnicalLevel) ?? 'intermediate'
    );
    const [responseLength, setResponseLength] = useState<ResponseLength>(
        (userInfo?.preferences?.responseLength as ResponseLength) ?? 'standard'
    );
    const [displayName, setDisplayName] = useState<string>(userInfo?.displayName ?? '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [savedAt, setSavedAt] = useState<string | null>(null);

    useEffect(() => {
        setTechnicalLevel((userInfo?.preferences?.technicalLevel as TechnicalLevel) ?? 'intermediate');
        setResponseLength((userInfo?.preferences?.responseLength as ResponseLength) ?? 'standard');
        setDisplayName(userInfo?.displayName ?? '');
    }, [userInfo]);

    async function save() {
        setSaving(true);
        setError(null);
        try {
            await apiCall('/me', {
                method: 'PATCH',
                body: { displayName, preferences: { technicalLevel, responseLength } }
            });
            await onRefresh();
            setSavedAt(new Date().toLocaleTimeString());
        } catch (err) {
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="admin-page">
            <header className="admin-page__header">
                <div>
                    <h1>Profile</h1>
                    <p className="admin-page__sub">Your account preferences. Saved to your profile.</p>
                </div>
            </header>
            <label className="form-group">
                <span className="form-label">Display name</span>
                <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder={userInfo?.email}
                    disabled={saving}
                />
            </label>
            <fieldset className="form-group form-group--fieldset">
                <legend className="form-label">Technical level — shapes how AI tailors answers</legend>
                <label className="radio-row">
                    <input type="radio" name="tl" checked={technicalLevel === 'novice'} onChange={() => setTechnicalLevel('novice')} disabled={saving} />
                    <span><strong>Novice</strong> — plain language, avoid jargon, explain acronyms</span>
                </label>
                <label className="radio-row">
                    <input type="radio" name="tl" checked={technicalLevel === 'intermediate'} onChange={() => setTechnicalLevel('intermediate')} disabled={saving} />
                    <span><strong>Intermediate</strong> — comfortable with domain terms, brief on basics</span>
                </label>
                <label className="radio-row">
                    <input type="radio" name="tl" checked={technicalLevel === 'expert'} onChange={() => setTechnicalLevel('expert')} disabled={saving} />
                    <span><strong>Expert</strong> — terse, technical, skip background</span>
                </label>
            </fieldset>
            <fieldset className="form-group form-group--fieldset">
                <legend className="form-label">AI response length</legend>
                <label className="radio-row">
                    <input type="radio" name="rl" checked={responseLength === 'concise'} onChange={() => setResponseLength('concise')} disabled={saving} />
                    <span><strong>Concise</strong> — short, just the answer (1–2 short paragraphs or bullets)</span>
                </label>
                <label className="radio-row">
                    <input type="radio" name="rl" checked={responseLength === 'standard'} onChange={() => setResponseLength('standard')} disabled={saving} />
                    <span><strong>Standard</strong> — balanced detail (a few paragraphs)</span>
                </label>
                <label className="radio-row">
                    <input type="radio" name="rl" checked={responseLength === 'thorough'} onChange={() => setResponseLength('thorough')} disabled={saving} />
                    <span><strong>Thorough</strong> — in-depth explanation with examples and context</span>
                </label>
            </fieldset>
            {error && <div className="form-error">{error}</div>}
            {savedAt && !error && <div className="form-success">Saved at {savedAt}</div>}
            <div className="comment-composer__actions">
                <button className="btn btn--primary" onClick={() => void save()} disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                </button>
            </div>
        </div>
    );
}
